import { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#111] border border-white/10 rounded-sm px-5 py-4 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#4169E1] focus:ring-offset-2 focus:ring-offset-black transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "general", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received", {
        description: "Thank you for reaching out. Our team will be in touch soon.",
      });
      setForm({ name: "", email: "", phone: "", subject: "general", message: "" });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again or call us directly." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 md:py-40 bg-[#0a0a0a] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#4169E1] mb-6">Connect With Us</p>
          <h2 className="font-display font-bold uppercase tracking-tighter leading-none text-4xl sm:text-5xl md:text-6xl">
            Plan your visit
          </h2>
          <p className="mt-8 text-base text-zinc-400 leading-relaxed max-w-md">
            Whether you're looking for a church home, need prayer, or just have a question — we would love
            to hear from you.
          </p>

          <div className="mt-12 space-y-8">
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 text-[#4169E1] mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-1">Address</p>
                <p data-testid="contact-address" className="text-base text-zinc-200">
                  6810 Presidential Drive, Jackson, MS 39213
                  <br />
                  <span className="text-zinc-500 text-sm">Presidential Hills Subdivision</span>
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-5 h-5 text-[#4169E1] mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-1">Phone</p>
                <a data-testid="contact-phone" href="tel:+17692086710" className="text-base text-zinc-200 hover:text-[#4169E1] transition-colors duration-300">
                  (769) 208-6710
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail className="w-5 h-5 text-[#4169E1] mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-1">Email</p>
                <a data-testid="contact-email" href="mailto:contact@hpvcjackson.org" className="text-base text-zinc-200 hover:text-[#4169E1] transition-colors duration-300">
                  contact@hpvcjackson.org
                </a>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form data-testid="contact-form" onSubmit={submit} className="space-y-6 bg-[#0d0d0d] border border-white/10 rounded-sm p-8 md:p-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <input data-testid="contact-form-name" required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="contact-form-email" required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <input data-testid="contact-form-phone" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} />
              <select data-testid="contact-form-subject" value={form.subject} onChange={set("subject")} className={inputCls}>
                <option value="general">General Question</option>
                <option value="prayer">Prayer Request</option>
                <option value="visit">Planning a Visit</option>
              </select>
            </div>
            <textarea
              data-testid="contact-form-message"
              required
              rows={5}
              placeholder="How can we serve you?"
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} resize-none`}
            />
            <button
              data-testid="contact-form-submit"
              type="submit"
              disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#4169E1] hover:bg-[#3555C0] disabled:opacity-60 text-white font-semibold uppercase tracking-widest text-sm px-8 py-4 rounded-sm transition-colors duration-300 hover:-translate-y-0.5"
            >
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              {sending ? "Sending..." : "Send Message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
